import React, { useEffect, useRef } from 'react';
import { ShieldAlert } from 'lucide-react';
import { useProfileSecurity } from '../hooks/useProfileSecurity';
import TwoFactorEnrollment from './TwoFactorEnrollment';

/**
 * Stops a member whose administrator set `mfa_required` until they have an
 * authenticator app registered.
 *
 * Enrollment cannot be done for someone else — the secret has to reach their
 * own phone — so this is what the administrator's "Require" button actually
 * does: it puts this screen in front of the person at their next sign-in.
 *
 * Whether they already hold a second factor is read from Hanko, not from the
 * flag. A member who enrolled on their own is let straight through and the flag
 * is cleared, so the requirement resolves itself instead of trapping someone
 * who has already complied.
 */
export default function TwoFactorGate({ onSatisfied }) {
    const {
        loading, busy, error, enroll, code, setCode,
        registered, startEnrollment, confirmEnrollment,
    } = useProfileSecurity();

    // Both the already-registered case and the just-registered case land on
    // `registered === true`; the latch keeps the release from firing twice.
    const releasedRef = useRef(false);

    useEffect(() => {
        if (registered !== true || releasedRef.current) return;
        releasedRef.current = true;
        onSatisfied();
    }, [registered, onSatisfied]);

    return (
        <div style={styles.gate}>
            <div style={styles.card}>
                <ShieldAlert size={30} style={{ color: '#FBBF24', marginBottom: 14 }} />
                <h2 style={styles.title}>Two-Factor Setup Required</h2>
                <p style={styles.desc}>
                    Your administrator requires an authenticator app on this account.
                    Register one to continue — it takes about a minute, and you will be
                    asked for a 6-digit code each time you sign in with your password.
                </p>

                {error && <p style={styles.error}>{error}</p>}

                {registered === true && <p style={styles.desc}>Registered — taking you to the console…</p>}

                {registered === false && !enroll && (
                    <button
                        onClick={startEnrollment}
                        disabled={busy === 'totp'}
                        style={{ ...styles.startBtn, ...(busy === 'totp' ? styles.startBtnDisabled : {}) }}
                    >
                        {busy === 'totp' ? 'Starting…' : 'Register 2FA'}
                    </button>
                )}

                {registered === null && (
                    <p style={styles.desc}>
                        {loading ? 'Checking your account…' : 'Your two-factor status could not be read. Please reload the page.'}
                    </p>
                )}

                {/* No cancel: leaving mid-enrollment would drop the person back
                    onto this gate anyway, and offering an out would make the
                    administrator's requirement advisory. */}
                <TwoFactorEnrollment
                    enroll={enroll}
                    code={code}
                    setCode={setCode}
                    busy={busy}
                    onConfirm={confirmEnrollment}
                />
            </div>
        </div>
    );
}

const styles = {
    gate: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#0F172A',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: 20,
    },
    card: {
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 16,
        padding: '40px 36px',
        maxWidth: 560,
        width: '100%',
        textAlign: 'center',
    },
    title: {
        color: '#F1F5F9',
        fontSize: 22,
        fontWeight: 700,
        margin: '0 0 12px',
    },
    desc: {
        color: '#94A3B8',
        fontSize: 14,
        lineHeight: 1.55,
        margin: '0 0 20px',
    },
    error: {
        color: '#FCA5A5',
        fontSize: 13,
        margin: '0 0 16px',
        padding: '10px 14px',
        backgroundColor: 'rgba(239, 68, 68, 0.12)',
        border: '1px solid rgba(239, 68, 68, 0.25)',
        borderRadius: 8,
        textAlign: 'left',
    },
    startBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '11px 26px',
        fontSize: 14,
        fontWeight: 600,
        color: '#FFFFFF',
        backgroundColor: '#3B82F6',
        border: 'none',
        borderRadius: 8,
        cursor: 'pointer',
        fontFamily: 'inherit',
    },
    startBtnDisabled: {
        backgroundColor: '#475569',
        cursor: 'not-allowed',
    },
};
